import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';

const Services = () => {
  return (
    <div className="services-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Our Services</h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Comprehensive palliative care tailored to your unique needs—from cancer pain management 
              to neurological conditions and end-of-life care
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all group">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white">{service.title}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4 line-clamp-3">{service.description}</p>
                  <Link to={`/services/${service.id}`}>
                    <Button variant="link" className="text-blue-600 hover:text-blue-700 p-0 group">
                      Learn More 
                      <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions We Treat */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Conditions We Support</h2>
              <p className="text-lg text-gray-600">
                Specialized care for a wide range of terminal and life-limiting conditions
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { category: "Cancer-Related", conditions: ["All cancer types", "Cancer pain", "Treatment side effects", "End-stage cancer"] },
                { category: "Neurological", conditions: ["Stroke", "Traumatic brain injury", "Dementia & Alzheimer's", "Parkinson's disease", "ALS"] },
                { category: "Brain & Consciousness", conditions: ["Coma", "Vegetative state", "Brain death", "Hypoxic brain injury"] },
                { category: "Other Terminal Conditions", conditions: ["End-stage organ failure", "Advanced respiratory disease", "Multiple chronic conditions"] }
              ].map((group, index) => (
                <Card key={index} className="border-l-4 border-l-blue-600 shadow-md">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{group.category}</h3>
                    <ul className="space-y-2">
                      {group.conditions.map((condition, i) => (
                        <li key={i} className="text-gray-600 flex items-start gap-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span>{condition}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-3xl p-12 text-white shadow-2xl">
            <h2 className="text-3xl font-bold mb-4">Need Guidance on the Right Care?</h2>
            <p className="text-lg mb-8 text-blue-50">
              Our team is here to help you understand your options and find the best support for your situation.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg rounded-full shadow-lg">
                Schedule a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
